import { useEffect, useState } from 'react'

const DEBOUNCE_MS = 2000

export const DOMAIN_CHECK_STATUS = {
  CHECKING: 'checking',
  VALID: 'valid',
  INVALID: 'invalid',
  ERROR: 'error'
}

/**
 * Returns null | 'checking' | 'valid' | 'invalid' | 'error'.
 *
 * 'error' is deliberately distinct from 'invalid': collapsing a failed lookup
 * into "not a business email" told people their own domain was rejected.
 */
export const useDomainChecker = (email) => {
  const [status, setStatus] = useState(null)

  useEffect(() => {
    if (!email || !email.includes('@') || email.length < 5) {
      setStatus(null)
      return undefined
    }

    // Canonicalize before asking: the server lowercases the address it derives
    // the domain from, so sending it as typed asked a different question.
    const domain = String(email).split('@').pop().trim().toLowerCase().replace(/\.+$/, '')
    if (!domain || !domain.includes('.')) {
      setStatus(null)
      return undefined
    }

    setStatus(DOMAIN_CHECK_STATUS.CHECKING)

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/check-domain', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain, skipRecaptcha: true })
        })

        if (cancelled) return
        if (res.status === 400 || res.status === 404) {
          setStatus(DOMAIN_CHECK_STATUS.INVALID)
          return
        }
        if (!res.ok) {
          setStatus(DOMAIN_CHECK_STATUS.ERROR)
          return
        }

        const payload = await res.json().catch(() => null)
        setStatus(payload?.status === 'success' ? DOMAIN_CHECK_STATUS.VALID : DOMAIN_CHECK_STATUS.INVALID)
      } catch (err) {
        if (cancelled) return
        setStatus(DOMAIN_CHECK_STATUS.ERROR)
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [email])

  return status
}
